import {
   Component,
   DestroyRef,
   inject,
   OnInit,
   Renderer2,
   DOCUMENT,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { RouterOutlet } from '@angular/router';
import { UpdateAppService } from './services/update-app.service';
import { UserService } from './user/user.service';
import { AsyncPipe, NgOptimizedImage } from '@angular/common';
import { TimezoneMismatchBannerComponent } from './components/timezone-mismatch-banner/timezone-mismatch-banner.component';
import {
   map,
   switchMap,
   timer,
   of,
   startWith,
   filter,
   distinctUntilChanged,
} from 'rxjs';

@Component({
   selector: 'app-root',
   standalone: true,
   imports: [RouterOutlet, AsyncPipe, NgOptimizedImage, TimezoneMismatchBannerComponent],
   templateUrl: './app.component.html',
   styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
   updateApp = inject(UpdateAppService);
   private userService = inject(UserService);
   private renderer = inject(Renderer2);
   private document = inject(DOCUMENT);
   private destroyRef = inject(DestroyRef);
   isInitialized$ = this.userService.isInitialized$;

   showLoading$ = this.isInitialized$.pipe(
      switchMap((initialized) => {
         if (initialized) return of(false);
         return timer(700).pipe(map(() => true));
      }),
      startWith(false),
   );

   constructor() {
      this.updateApp.checkUpdate();
   }

   ngOnInit() {
      // currentStore$ is a live Firestore docData listener that emits on every
      // change to the store document. Only re-apply the font-size class when the
      // value actually changes — otherwise every store update forces a
      // full-document style recalc via the root <html> class.
      this.userService.currentStore$
         .pipe(
            filter((store) => !!store),
            map((store) => store!.fontSize || 'medium'),
            distinctUntilChanged(),
            takeUntilDestroyed(this.destroyRef),
         )
         .subscribe((fontSize) => this.applyFontSize(fontSize));
   }

   private applyFontSize(fontSize: string) {
      const root = this.document.documentElement;
      this.renderer.removeClass(root, 'font-small');
      this.renderer.removeClass(root, 'font-medium');
      this.renderer.removeClass(root, 'font-large');
      this.renderer.removeClass(root, 'font-extra-large');
      this.renderer.addClass(root, `font-${fontSize}`);
   }
}
